import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { getLoanCampaign, updateLoanCampaign, uploadLoanLogo } from '../services/loanService';
import { getCategories } from '../services/categoryService';
import ProductLoanBadge from './ProductLoanBadge';
import ProductLoanWidget from './ProductLoanWidget';
import sampleCardImg from '../assets/default.png';

const PARTNER_PRESETS = [
  {
    name: 'Iute Pay',
    logo: 'https://iute.al/wp-content/uploads/2021/11/IUTE-Logo-e1636545234553.png',
    title: 'Bli me Këste me Iute Pay',
    subtitle: '0% Interes • Aprovim në pak minuta me ID',
    bgColor: '#007da5',
    textColor: '#ffffff'
  },
  {
    name: 'BKT Prima',
    logo: '',
    title: 'Kartë Krediti BKT Prima',
    subtitle: '0% Interes nga 2 deri në 12 muaj',
    bgColor: '#b91c1c',
    textColor: '#ffffff'
  },
  {
    name: 'Raiffeisen Bank',
    logo: '',
    title: 'Financim me Raiffeisen Bank',
    subtitle: 'Këste mujore të leverdisshme për pajisjet tuaja',
    bgColor: '#eab308',
    textColor: '#0f172a'
  },
  {
    name: 'OTP Bank',
    logo: '',
    title: 'Pagesë me Këste me OTP Bank',
    subtitle: '0% Interes për 12 ose 24 muaj',
    bgColor: '#15803d',
    textColor: '#ffffff'
  },
  {
    name: '0% Interes (Gjenerike)',
    logo: '',
    title: 'Mundësi Blerje me Këste 0% Interes',
    subtitle: 'Merr pajisjen sot, paguaj me këste të lehta',
    bgColor: '#0f172a',
    textColor: '#ffffff'
  }
];

export default function AdminLoanCampaignTab({ categories: propCategories }) {
  const [campaign, setCampaign] = useState(null);
  const [categories, setCategories] = useState(propCategories || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Active Rule Editor State
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [catSearch, setCatSearch] = useState('');
  const [bulkSkuInput, setBulkSkuInput] = useState('');
  const [skuSearch, setSkuSearch] = useState('');

  // Live preview mode: 'card' or 'productView'
  const [previewMode, setPreviewMode] = useState('card');
  const [previewPrice, setPreviewPrice] = useState(36000);

  const fileInputRef = useRef(null);

  // Load campaign and categories
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const [campData, catsData] = await Promise.all([
          getLoanCampaign(true).catch(() => null),
          propCategories ? Promise.resolve(propCategories) : getCategories().catch(() => [])
        ]);
        if (!mounted) return;
        if (campData) {
          setCampaign(campData);
          if (campData.rules && campData.rules.length > 0) {
            setEditingRuleId(campData.rules[0].id);
          }
        }
        if (Array.isArray(catsData) && catsData.length) {
          setCategories(catsData);
        }
      } catch (err) {
        console.error('Failed to load loan campaign tab:', err);
        toast.error('Dështoi ngarkimi i fushatës së kredisë');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [propCategories]);

  // Flatten categories
  const { flatCategories } = useMemo(() => {
    const flat = [];
    function walk(nodes, depth = 0, path = []) {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n || !n.id) continue;
        const currentPath = [...path, n.name];
        const item = {
          id: String(n.id),
          name: n.name,
          depth,
          path: currentPath,
          subcategories: Array.isArray(n.subcategories) ? n.subcategories : []
        };
        flat.push(item);
        if (item.subcategories.length) {
          walk(item.subcategories, depth + 1, currentPath);
        }
      }
    }
    walk(categories);
    return { flatCategories: flat };
  }, [categories]);

  // Filter categories in editor search
  const filteredCategories = useMemo(() => {
    if (!catSearch.trim()) return flatCategories;
    const q = catSearch.toLowerCase().trim();
    return flatCategories.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.id.includes(q) ||
      c.path.some(p => p.toLowerCase().includes(q))
    );
  }, [flatCategories, catSearch]);

  const activeRule = useMemo(() => {
    if (!campaign || !campaign.rules) return null;
    return campaign.rules.find(r => r.id === editingRuleId) || campaign.rules[0] || null;
  }, [campaign, editingRuleId]);

  // Update whole campaign object
  const handleUpdateActiveRule = (updater) => {
    if (!activeRule) return;
    setCampaign(prev => {
      const nextRules = prev.rules.map(r => {
        if (r.id === activeRule.id) {
          return typeof updater === 'function' ? updater(r) : { ...r, ...updater };
        }
        return r;
      });
      return { ...prev, rules: nextRules };
    });
  };

  // Add new rule
  const handleAddNewRule = () => {
    const newRule = {
      id: `loan_rule_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      name: `Rregull i Ri Kredie (${(campaign?.rules?.length || 0) + 1})`,
      enabled: true,
      partnerName: 'Iute Pay',
      partnerLogo: '',
      terms: [
        { months: 12, enabled: true, interestRate: 0, label: '12 Muaj' },
        { months: 36, enabled: true, interestRate: 0, label: '36 Muaj' }
      ],
      categoryIds: [],
      includeSubcategories: true,
      skus: [],
      minPrice: 0,
      card: {
        enabled: true,
        displayMode: 'badge',
        selectedTerm: 12,
        badgeText: 'Nga {monthly}/muaj me {months} këste',
        subpriceText: 'nga {monthly}/muaj ({months} këste)',
        position: 'top-left',
        offsetX: 10,
        offsetY: 10,
        bgColor: '#007da5',
        textColor: '#ffffff',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        shadow: true
      },
      productView: {
        enabled: true,
        showBadge: false,
        badgePosition: 'top-left',
        badgeOffsetX: 10,
        badgeOffsetY: 10,
        badgeBgColor: '#007da5',
        badgeTextColor: '#ffffff',
        badgeText: 'Me Këste: nga {monthly}/muaj ({months} muaj)',
        showBox: true,
        boxTitle: 'Mundësi Blerje me Këste 0% Interes',
        boxSubtitle: 'Paguaj me këste të favorshme në bashkëpunim me {partner}',
        defaultSelectedTerm: 12,
        showTermsSelector: true,
        note: 'Apliko lehtësisht me kartë identiteti në arkë ose online gjatë porosisë.'
      }
    };

    setCampaign(prev => ({
      ...prev,
      rules: [...(prev?.rules || []), newRule]
    }));
    setEditingRuleId(newRule.id);
    toast.success('U shtua një rregull i ri!');
  };

  // Duplicate rule
  const handleDuplicateRule = (ruleToCopy) => {
    const copy = {
      ...JSON.parse(JSON.stringify(ruleToCopy)),
      id: `loan_rule_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      name: `${ruleToCopy.name} (Kopje)`
    };
    setCampaign(prev => ({
      ...prev,
      rules: [...prev.rules, copy]
    }));
    setEditingRuleId(copy.id);
    toast.success('Rregulli u duplikua me sukses');
  };

  // Delete rule
  const handleDeleteRule = (ruleId, ruleName) => {
    if (campaign.rules.length <= 1) {
      toast.error('Duhet të ekzistojë të paktën 1 rregull në fushatë.');
      return;
    }
    if (!window.confirm(`Jeni të sigurt që dëshironi të fshini rregullin "${ruleName}"?`)) {
      return;
    }
    const remaining = campaign.rules.filter(r => r.id !== ruleId);
    setCampaign(prev => ({ ...prev, rules: remaining }));
    setEditingRuleId(remaining[0].id);
    toast.success(`Rregulli "${ruleName}" u fshi`);
  };

  // Save changes to backend
  const handleSaveCampaign = async () => {
    try {
      setSaving(true);
      await updateLoanCampaign(campaign);
      toast.success('Konfigurimi i kredisë me këste u ruajt me sukses!');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Dështoi ruajtja e konfigurimit');
    } finally {
      setSaving(false);
    }
  };

  // Logo upload
  const handleLogoUpload = async (file) => {
    if (!file) return;
    try {
      setUploadingLogo(true);
      const res = await uploadLoanLogo(file);
      if (res && res.url) {
        handleUpdateActiveRule({ partnerLogo: res.url });
        toast.success('Logoja u ngarkua me sukses!');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Dështoi ngarkimi i logos');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Preset apply
  const handleApplyPreset = (preset) => {
    handleUpdateActiveRule(prev => ({
      partnerName: preset.name,
      partnerLogo: preset.logo || prev.partnerLogo,
      card: {
        ...prev.card,
        bgColor: preset.bgColor,
        textColor: preset.textColor
      },
      productView: {
        ...prev.productView,
        boxTitle: preset.title,
        boxSubtitle: preset.subtitle,
        badgeBgColor: preset.bgColor,
        badgeTextColor: preset.textColor
      }
    }));
    toast.success(`U aplikua profili "${preset.name}"`);
  };

  // Add bulk SKUs
  const handleAddBulkSkus = () => {
    if (!bulkSkuInput.trim() || !activeRule) return;
    const tokens = bulkSkuInput
      .split(/[\r\n,;\s]+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => s.length >= 2);

    const merged = Array.from(new Set([...(activeRule.skus || []), ...tokens]));
    handleUpdateActiveRule({ skus: merged });
    setBulkSkuInput('');
    toast.success(`U shtuan ${tokens.length} kode SKU!`);
  };

  const handleRemoveSku = (skuToRemove) => {
    if (!activeRule) return;
    const updated = (activeRule.skus || []).filter(s => s !== skuToRemove);
    handleUpdateActiveRule({ skus: updated });
  };

  // Mock sample evaluation for live preview
  const sampleEvaluation = useMemo(() => {
    if (!activeRule) return null;
    const price = Number(previewPrice) || 36000;
    const terms = (activeRule.terms || []).filter(t => t.enabled).map(t => {
      const months = Number(t.months) || 12;
      const interest = Number(t.interestRate) || 0;
      const totalAmount = interest > 0 ? price * (1 + interest / 100) : price;
      const monthlyAmount = Math.round(totalAmount / months);
      return {
        months,
        label: t.label || `${months} Muaj`,
        interestRate: interest,
        totalAmount,
        monthlyAmount,
        monthlyFormatted: `${monthlyAmount.toLocaleString('sq-AL')} L`,
        totalFormatted: `${totalAmount.toLocaleString('sq-AL')} L`
      };
    });

    const term12 = terms.find(t => t.months === 12);
    const term36 = terms.find(t => t.months === 36);
    const lowest = terms.reduce((min, t) => t.monthlyAmount < min.monthlyAmount ? t : min, terms[0]);

    let cardTerm = lowest;
    if (activeRule.card?.selectedTerm === 12 && term12) cardTerm = term12;
    if (activeRule.card?.selectedTerm === 36 && term36) cardTerm = term36;

    const cardMonthly = cardTerm ? cardTerm.monthlyFormatted : '2,500 L';
    const cardMonths = cardTerm ? cardTerm.months : 12;

    const replaceTokens = (txt) => {
      return (txt || '')
        .replace(/\{monthly\}/g, cardMonthly)
        .replace(/\{months\}/g, String(cardMonths))
        .replace(/\{partner\}/g, activeRule.partnerName || 'Iute Pay')
        .replace(/\{price\}/g, `${price.toLocaleString('sq-AL')} L`);
    };

    return {
      isEligible: true,
      ruleName: activeRule.name,
      partnerName: activeRule.partnerName,
      partnerLogo: activeRule.partnerLogo,
      terms,
      card: {
        ...activeRule.card,
        badgeText: replaceTokens(activeRule.card?.badgeText),
        subpriceText: replaceTokens(activeRule.card?.subpriceText)
      },
      productView: {
        ...activeRule.productView,
        badgeText: replaceTokens(activeRule.productView?.badgeText),
        boxTitle: replaceTokens(activeRule.productView?.boxTitle),
        boxSubtitle: replaceTokens(activeRule.productView?.boxSubtitle),
        defaultSelectedTerm: terms.find(t => t.months === Number(activeRule.productView?.defaultSelectedTerm)) || terms[0]
      }
    };
  }, [activeRule, previewPrice]);

  if (loading || !campaign) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#64748b' }}>
        Duke ngarkuar konfigurimin e kredisë dhe kësteve...
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 24 }}>

      {/* Main Campaign Header & Master Switch */}
      <div className="admin-card" style={{
        padding: 22,
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#0f172a' }}>
              💳 Fushata e Pagesës me Këste (12 / 36 Muaj)
            </h3>
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              background: campaign.enabled ? '#dcfce7' : '#fee2e2',
              color: campaign.enabled ? '#166534' : '#991b1b',
              border: `1px solid ${campaign.enabled ? '#bbf7d0' : '#fecaca'}`,
              padding: '2px 10px',
              borderRadius: 12
            }}>
              {campaign.enabled ? 'AKTIVE' : 'JO AKTIVE'}
            </span>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, color: '#64748b', maxWidth: 850 }}>
            Përcaktoni mundësinë e blerjes me këste (12 muaj ose 36 muaj) sipas kategorive ose kodeve SKU (logjika e stickers).
            Konfiguroni pavarur mënyrën e shfaqjes në <strong>Kartat e Produkteve</strong> dhe në <strong>Faqen e Detajuar (Product View)</strong>.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Master Switch */}
          <div
            onClick={() => setCampaign(prev => ({ ...prev, enabled: !prev.enabled }))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              userSelect: 'none',
              padding: '8px 14px',
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              borderRadius: 10
            }}
          >
            <div style={{
              width: 44,
              height: 24,
              borderRadius: 12,
              background: campaign.enabled ? '#007da5' : '#cbd5e1',
              position: 'relative',
              transition: 'background 0.2s'
            }}>
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#fff',
                position: 'absolute',
                top: 2,
                left: campaign.enabled ? 22 : 2,
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
              {campaign.enabled ? 'Aktivizuar' : 'Çaktivizuar'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleSaveCampaign}
            disabled={saving}
            style={{
              background: '#007da5',
              color: '#fff',
              border: 'none',
              padding: '10px 24px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,125,165,0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {saving ? 'Duke ruajtur...' : '💾 Ruaj Ndryshimet'}
          </button>
        </div>
      </div>

      {/* Rules Manager Tabs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        background: '#f1f5f9',
        padding: '10px 14px',
        borderRadius: 12,
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginRight: 4 }}>
            Rregullat e Kredisë:
          </span>
          {campaign.rules.map((rule, idx) => {
            const isSelected = rule.id === editingRuleId;
            return (
              <button
                key={rule.id}
                type="button"
                onClick={() => setEditingRuleId(rule.id)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: isSelected ? '2px solid #007da5' : '1px solid #cbd5e1',
                  background: isSelected ? '#ffffff' : '#f8fafc',
                  color: isSelected ? '#007da5' : '#334155',
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: isSelected ? '0 2px 6px rgba(0,125,165,0.15)' : 'none'
                }}
              >
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: rule.enabled ? '#10b981' : '#cbd5e1'
                }} />
                <span>{rule.name || `Rregull ${idx + 1}`}</span>
                {rule.partnerName && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>({rule.partnerName})</span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleAddNewRule}
          style={{
            background: '#ffffff',
            color: '#007da5',
            border: '1.5px dashed #007da5',
            padding: '7px 14px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <span>➕</span>
          <span>Shto Rregull të Ri</span>
        </button>
      </div>

      {activeRule && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(360px, 1fr)', gap: 24, alignItems: 'start' }}>

          {/* LEFT: Rule Configuration Editor */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* SECTION 1: Rule Details & Partner Branding */}
            <div className="admin-card" style={{ padding: 22, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  1. Të Dhënat e Rregullit & Partneri Financiar
                </h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => handleDuplicateRule(activeRule)}
                    style={{ background: '#f8fafc', border: '1px solid #cbd5e1', padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Kopjo Rregullin
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteRule(activeRule.id, activeRule.name)}
                    style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Fshi
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <label className="admin-label">Emri i Rregullit (Identifikues në Admin)</label>
                  <input
                    className="admin-input"
                    value={activeRule.name}
                    onChange={(e) => handleUpdateActiveRule({ name: e.target.value })}
                    placeholder="p.sh. Kredi 12 & 36 Muaj me Iute"
                  />
                </div>
                <div>
                  <label className="admin-label">Statusi i Rregullit</label>
                  <select
                    className="admin-select"
                    value={activeRule.enabled ? 'true' : 'false'}
                    onChange={(e) => handleUpdateActiveRule({ enabled: e.target.value === 'true' })}
                  >
                    <option value="true">Aktiv (Shfaqet)</option>
                    <option value="false">Jo aktiv (I fshehur)</option>
                  </select>
                </div>
              </div>

              {/* Partner Presets */}
              <div style={{ marginBottom: 16 }}>
                <label className="admin-label" style={{ marginBottom: 6 }}>
                  Zgjidh Partnerin / Institucionin Financiar (Presets të Shpejta):
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {PARTNER_PRESETS.map(p => (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => handleApplyPreset(p)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: activeRule.partnerName === p.name ? '2px solid #007da5' : '1px solid #cbd5e1',
                        background: activeRule.partnerName === p.name ? '#e0f2fe' : '#ffffff',
                        color: activeRule.partnerName === p.name ? '#0369a1' : '#334155',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label className="admin-label">Emri i Partnerit (shfaqet tek etiketat dhe widget-i)</label>
                  <input
                    className="admin-input"
                    value={activeRule.partnerName || ''}
                    onChange={(e) => handleUpdateActiveRule({ partnerName: e.target.value })}
                    placeholder="p.sh. Iute Pay, BKT Prima, etj."
                  />
                </div>

                <div>
                  <label className="admin-label">Logo e Partnerit (URL ose Ngarko Skedar)</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      className="admin-input"
                      value={activeRule.partnerLogo || ''}
                      onChange={(e) => handleUpdateActiveRule({ partnerLogo: e.target.value })}
                      placeholder="/uploads/stickers/... ose URL"
                      style={{ flex: 1 }}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept="image/*,.svg,.webp"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleLogoUpload(e.target.files[0]);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      disabled={uploadingLogo}
                      style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        padding: '8px 12px',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {uploadingLogo ? '...' : 'Ngarko'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: Loan Terms (12 Muaj & 36 Muaj) */}
            <div className="admin-card" style={{ padding: 22, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  2. Afatet e Kredisë (12 Muaj & 36 Muaj)
                </h4>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Çmimi / muajt e përzgjedhur (pa interes ose me interes)
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* 12 Months Card */}
                {(() => {
                  const t12 = (activeRule.terms || []).find(t => t.months === 12);
                  const isEnabled = t12 ? t12.enabled : true;
                  return (
                    <div style={{
                      padding: 14,
                      borderRadius: 10,
                      border: isEnabled ? '1.5px solid #007da5' : '1px solid #e2e8f0',
                      background: isEnabled ? '#f0f9ff' : '#f8fafc'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            id="term_12_check"
                            onChange={(e) => {
                              const checked = e.target.checked;
                              handleUpdateActiveRule(prev => {
                                const nextTerms = prev.terms.map(t => t.months === 12 ? { ...t, enabled: checked } : t);
                                return { ...prev, terms: nextTerms };
                              });
                            }}
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                          <label htmlFor="term_12_check" style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer', color: '#0f172a' }}>
                            Afati 12 Muaj
                          </label>
                        </div>
                        <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                          12 Këste
                        </span>
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Etiketa e Afatit</label>
                          <input
                            className="admin-input"
                            value={t12?.label || '12 Muaj'}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleUpdateActiveRule(prev => ({
                                ...prev,
                                terms: prev.terms.map(t => t.months === 12 ? { ...t, label: val } : t)
                              }));
                            }}
                            placeholder="12 Muaj"
                            style={{ padding: '6px 10px', fontSize: 13 }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Përqindja e Interesit (%) - 0 për Pa Interes</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="admin-input"
                            value={t12?.interestRate ?? 0}
                            onChange={(e) => {
                              const val = Number(e.target.value) || 0;
                              handleUpdateActiveRule(prev => ({
                                ...prev,
                                terms: prev.terms.map(t => t.months === 12 ? { ...t, interestRate: val } : t)
                              }));
                            }}
                            style={{ padding: '6px 10px', fontSize: 13 }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 36 Months Card */}
                {(() => {
                  const t36 = (activeRule.terms || []).find(t => t.months === 36);
                  const isEnabled = t36 ? t36.enabled : true;
                  return (
                    <div style={{
                      padding: 14,
                      borderRadius: 10,
                      border: isEnabled ? '1.5px solid #007da5' : '1px solid #e2e8f0',
                      background: isEnabled ? '#f0f9ff' : '#f8fafc'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            id="term_36_check"
                            onChange={(e) => {
                              const checked = e.target.checked;
                              handleUpdateActiveRule(prev => {
                                const nextTerms = prev.terms.map(t => t.months === 36 ? { ...t, enabled: checked } : t);
                                return { ...prev, terms: nextTerms };
                              });
                            }}
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                          <label htmlFor="term_36_check" style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer', color: '#0f172a' }}>
                            Afati 36 Muaj
                          </label>
                        </div>
                        <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                          36 Këste
                        </span>
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Etiketa e Afatit</label>
                          <input
                            className="admin-input"
                            value={t36?.label || '36 Muaj'}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleUpdateActiveRule(prev => ({
                                ...prev,
                                terms: prev.terms.map(t => t.months === 36 ? { ...t, label: val } : t)
                              }));
                            }}
                            placeholder="36 Muaj"
                            style={{ padding: '6px 10px', fontSize: 13 }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Përqindja e Interesit (%) - 0 për Pa Interes</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="admin-input"
                            value={t36?.interestRate ?? 0}
                            onChange={(e) => {
                              const val = Number(e.target.value) || 0;
                              handleUpdateActiveRule(prev => ({
                                ...prev,
                                terms: prev.terms.map(t => t.months === 36 ? { ...t, interestRate: val } : t)
                              }));
                            }}
                            style={{ padding: '6px 10px', fontSize: 13 }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* SECTION 3: Card Settings (Karta e Produktit) */}
            <div className="admin-card" style={{ padding: 22, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                    3. Konfigurimi për Kartën e Produktit (Product Card)
                  </h4>
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#64748b' }}>
                    Zgjidhni mënyrën se si shfaqet informacioni në listimet e produkteve (badge mbi foto dhe/ose nën çmim).
                  </p>
                </div>
                <div
                  onClick={() => handleUpdateActiveRule(prev => ({
                    ...prev,
                    card: { ...prev.card, enabled: !prev.card.enabled }
                  }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: activeRule.card.enabled ? '#007da5' : '#64748b' }}>
                    {activeRule.card.enabled ? 'Aktiv në Kartë' : 'Jo aktiv'}
                  </span>
                  <div style={{
                    width: 38,
                    height: 20,
                    borderRadius: 10,
                    background: activeRule.card.enabled ? '#007da5' : '#cbd5e1',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 2,
                      left: activeRule.card.enabled ? 20 : 2,
                      transition: 'left 0.2s'
                    }} />
                  </div>
                </div>
              </div>

              {activeRule.card.enabled && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label className="admin-label">Mënyra e Shfaqjes në Kartë</label>
                      <select
                        className="admin-select"
                        value={activeRule.card.displayMode || 'badge'}
                        onChange={(e) => handleUpdateActiveRule(prev => ({
                          ...prev,
                          card: { ...prev.card, displayMode: e.target.value }
                        }))}
                      >
                        <option value="badge">Sticker Badge mbi Foto (Overlay)</option>
                        <option value="subprice">Tekst me Këst Mujor nën Çmim</option>
                        <option value="both">Të Dyja (Badge mbi foto + Tekst nën çmim)</option>
                      </select>
                    </div>

                    <div>
                      <label className="admin-label">Afati për Llogaritje në Kartë</label>
                      <select
                        className="admin-select"
                        value={activeRule.card.selectedTerm || 12}
                        onChange={(e) => {
                          const val = e.target.value === 'lowest' || e.target.value === 'highest'
                            ? e.target.value
                            : Number(e.target.value);
                          handleUpdateActiveRule(prev => ({
                            ...prev,
                            card: { ...prev.card, selectedTerm: val }
                          }));
                        }}
                      >
                        <option value={12}>12 Muaj (p.sh. nga 3,000 L/muaj)</option>
                        <option value={36}>36 Muaj (p.sh. nga 1,000 L/muaj)</option>
                        <option value="lowest">Kësti më i ulët (automatike sipas muajve maksimalë)</option>
                      </select>
                    </div>
                  </div>

                  {/* Badge Text & Subprice Text */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {(activeRule.card.displayMode === 'badge' || activeRule.card.displayMode === 'both') && (
                      <div>
                        <label className="admin-label">Teksti i Badge mbi Foto</label>
                        <input
                          className="admin-input"
                          value={activeRule.card.badgeText || ''}
                          onChange={(e) => handleUpdateActiveRule(prev => ({
                            ...prev,
                            card: { ...prev.card, badgeText: e.target.value }
                          }))}
                          placeholder="Nga {monthly}/muaj me {months} këste"
                        />
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                          Placeholder-at: <code>{'{monthly}'}</code>, <code>{'{months}'}</code>, <code>{'{partner}'}</code>
                        </div>
                      </div>
                    )}

                    {(activeRule.card.displayMode === 'subprice' || activeRule.card.displayMode === 'both') && (
                      <div>
                        <label className="admin-label">Teksti i Këstit nën Çmim</label>
                        <input
                          className="admin-input"
                          value={activeRule.card.subpriceText || ''}
                          onChange={(e) => handleUpdateActiveRule(prev => ({
                            ...prev,
                            card: { ...prev.card, subpriceText: e.target.value }
                          }))}
                          placeholder="nga {monthly}/muaj ({months} këste)"
                        />
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                          Placeholder-at: <code>{'{monthly}'}</code>, <code>{'{months}'}</code>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Badge Position & Styling */}
                  {(activeRule.card.displayMode === 'badge' || activeRule.card.displayMode === 'both') && (
                    <div style={{
                      padding: 12,
                      background: '#f8fafc',
                      borderRadius: 10,
                      border: '1px solid #e2e8f0',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: 12
                    }}>
                      <div>
                        <label className="admin-label">Pozicioni në Kënd</label>
                        <select
                          className="admin-select"
                          value={activeRule.card.position || 'top-left'}
                          onChange={(e) => handleUpdateActiveRule(prev => ({
                            ...prev,
                            card: { ...prev.card, position: e.target.value }
                          }))}
                        >
                          <option value="top-left">Lart Majtas (Top-Left)</option>
                          <option value="top-right">Lart Djathtas (Top-Right)</option>
                          <option value="bottom-left">Poshtë Majtas (Bottom-Left)</option>
                          <option value="bottom-right">Poshtë Djathtas (Bottom-Right)</option>
                        </select>
                      </div>

                      <div>
                        <label className="admin-label">Distanca X (px)</label>
                        <input
                          type="number"
                          className="admin-input"
                          value={activeRule.card.offsetX ?? 10}
                          onChange={(e) => handleUpdateActiveRule(prev => ({
                            ...prev,
                            card: { ...prev.card, offsetX: Number(e.target.value) || 0 }
                          }))}
                        />
                      </div>

                      <div>
                        <label className="admin-label">Distanca Y (px)</label>
                        <input
                          type="number"
                          className="admin-input"
                          value={activeRule.card.offsetY ?? 10}
                          onChange={(e) => handleUpdateActiveRule(prev => ({
                            ...prev,
                            card: { ...prev.card, offsetY: Number(e.target.value) || 0 }
                          }))}
                        />
                      </div>

                      <div>
                        <label className="admin-label">Ngjyra e Sfondit</label>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="color"
                            value={activeRule.card.bgColor || '#007da5'}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              card: { ...prev.card, bgColor: e.target.value }
                            }))}
                            style={{ width: 34, height: 34, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                          />
                          <input
                            className="admin-input"
                            value={activeRule.card.bgColor || '#007da5'}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              card: { ...prev.card, bgColor: e.target.value }
                            }))}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="admin-label">Ngjyra e Tekstit</label>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="color"
                            value={activeRule.card.textColor || '#ffffff'}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              card: { ...prev.card, textColor: e.target.value }
                            }))}
                            style={{ width: 34, height: 34, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                          />
                          <input
                            className="admin-input"
                            value={activeRule.card.textColor || '#ffffff'}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              card: { ...prev.card, textColor: e.target.value }
                            }))}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* SECTION 4: Product View Settings (Pamja e Detajuar) */}
            <div className="admin-card" style={{ padding: 22, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                    4. Konfigurimi për Pamjen e Produktit (Single Product View)
                  </h4>
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#64748b' }}>
                    Konfiguroni bllokun/widget-in me kalkulimin e plotë të kësteve pranë butonit të blerjes ose badge mbi foton kryesore.
                  </p>
                </div>
                <div
                  onClick={() => handleUpdateActiveRule(prev => ({
                    ...prev,
                    productView: { ...prev.productView, enabled: !prev.productView.enabled }
                  }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: activeRule.productView.enabled ? '#007da5' : '#64748b' }}>
                    {activeRule.productView.enabled ? 'Aktiv në Product View' : 'Jo aktiv'}
                  </span>
                  <div style={{
                    width: 38,
                    height: 20,
                    borderRadius: 10,
                    background: activeRule.productView.enabled ? '#007da5' : '#cbd5e1',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 2,
                      left: activeRule.productView.enabled ? 20 : 2,
                      transition: 'left 0.2s'
                    }} />
                  </div>
                </div>
              </div>

              {activeRule.productView.enabled && (
                <div style={{ display: 'grid', gap: 16 }}>

                  {/* Widget Settings in Buybox */}
                  <div style={{ padding: 14, background: '#f0fdfa', borderRadius: 10, border: '1px solid #99f6e4' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          id="pv_box_check"
                          checked={activeRule.productView.showBox !== false}
                          onChange={(e) => handleUpdateActiveRule(prev => ({
                            ...prev,
                            productView: { ...prev.productView, showBox: e.target.checked }
                          }))}
                          style={{ width: 18, height: 18, cursor: 'pointer' }}
                        />
                        <label htmlFor="pv_box_check" style={{ fontWeight: 700, fontSize: 14, color: '#0f766e', cursor: 'pointer' }}>
                          Shfaq Bllokun / Widget-in me Kalkulator Këstesh në Buybox
                        </label>
                      </div>
                      <span style={{ fontSize: 11, background: '#ccfbf1', color: '#0f766e', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                        Rekomanduar
                      </span>
                    </div>

                    {activeRule.productView.showBox !== false && (
                      <div style={{ display: 'grid', gap: 12, marginTop: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label className="admin-label">Titulli i Bllokut</label>
                            <input
                              className="admin-input"
                              value={activeRule.productView.boxTitle || ''}
                              onChange={(e) => handleUpdateActiveRule(prev => ({
                                ...prev,
                                productView: { ...prev.productView, boxTitle: e.target.value }
                              }))}
                              placeholder="Mundësi Blerje me Këste 0% Interes"
                            />
                          </div>

                          <div>
                            <label className="admin-label">Nëntitulli / Përshkrimi i Shkurtër</label>
                            <input
                              className="admin-input"
                              value={activeRule.productView.boxSubtitle || ''}
                              onChange={(e) => handleUpdateActiveRule(prev => ({
                                ...prev,
                                productView: { ...prev.productView, boxSubtitle: e.target.value }
                              }))}
                              placeholder="Paguaj me këste të favorshme në bashkëpunim me {partner}"
                            />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label className="admin-label">Afati i Parazgjedhur në Hapje</label>
                            <select
                              className="admin-select"
                              value={activeRule.productView.defaultSelectedTerm || 12}
                              onChange={(e) => handleUpdateActiveRule(prev => ({
                                ...prev,
                                productView: { ...prev.productView, defaultSelectedTerm: Number(e.target.value) }
                              }))}
                            >
                              <option value={12}>12 Muaj</option>
                              <option value={36}>36 Muaj</option>
                            </select>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22 }}>
                            <input
                              type="checkbox"
                              id="pv_selector_check"
                              checked={activeRule.productView.showTermsSelector !== false}
                              onChange={(e) => handleUpdateActiveRule(prev => ({
                                ...prev,
                                productView: { ...prev.productView, showTermsSelector: e.target.checked }
                              }))}
                              style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                            <label htmlFor="pv_selector_check" style={{ fontSize: 13, color: '#334155', cursor: 'pointer', fontWeight: 500 }}>
                              Shfaq tabet interaktive të përzgjedhjes (12m vs 36m)
                            </label>
                          </div>
                        </div>

                        <div>
                          <label className="admin-label">Shënim Sqarues në Fund të Widget-it</label>
                          <input
                            className="admin-input"
                            value={activeRule.productView.note || ''}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              productView: { ...prev.productView, note: e.target.value }
                            }))}
                            placeholder="Apliko lehtësisht me kartë identiteti në arkë ose online gjatë porosisë."
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Badge on main product image */}
                  <div style={{ padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        id="pv_badge_check"
                        checked={Boolean(activeRule.productView.showBadge)}
                        onChange={(e) => handleUpdateActiveRule(prev => ({
                          ...prev,
                          productView: { ...prev.productView, showBadge: e.target.checked }
                        }))}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                      <label htmlFor="pv_badge_check" style={{ fontWeight: 700, fontSize: 13.5, color: '#334155', cursor: 'pointer' }}>
                        Shfaq gjithashtu Sticker Badge mbi Foton Kryesore të Produktit
                      </label>
                    </div>

                    {activeRule.productView.showBadge && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 10 }}>
                        <div>
                          <label className="admin-label">Teksti mbi Foto</label>
                          <input
                            className="admin-input"
                            value={activeRule.productView.badgeText || ''}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              productView: { ...prev.productView, badgeText: e.target.value }
                            }))}
                          />
                        </div>
                        <div>
                          <label className="admin-label">Pozicioni</label>
                          <select
                            className="admin-select"
                            value={activeRule.productView.badgePosition || 'top-left'}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              productView: { ...prev.productView, badgePosition: e.target.value }
                            }))}
                          >
                            <option value="top-left">Lart Majtas</option>
                            <option value="top-right">Lart Djathtas</option>
                            <option value="bottom-left">Poshtë Majtas</option>
                            <option value="bottom-right">Poshtë Djathtas</option>
                          </select>
                        </div>
                        <div>
                          <label className="admin-label">Sfondi</label>
                          <input
                            type="color"
                            value={activeRule.productView.badgeBgColor || '#007da5'}
                            onChange={(e) => handleUpdateActiveRule(prev => ({
                              ...prev,
                              productView: { ...prev.productView, badgeBgColor: e.target.value }
                            }))}
                            style={{ width: '100%', height: 36, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 5: Targeting (Logjika e Stickers) */}
            <div className="admin-card" style={{ padding: 22, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  5. Zgjedhja e Produkteve (Logjika e Stickers: Kategori & Kode SKU)
                </h4>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#64748b' }}>
                  Nëse nuk zgjidhni asnjë kategori apo SKU, rregulli aplikohet mbi <strong>të gjitha produktet</strong> e dyqanit.
                </p>
              </div>

              {/* Min price filter */}
              <div style={{ marginBottom: 16, maxWidth: 300 }}>
                <label className="admin-label">Çmimi Minimal i Produktit për të Ofrur Kredi (Lekë)</label>
                <input
                  type="number"
                  min="0"
                  step="500"
                  className="admin-input"
                  value={activeRule.minPrice || 0}
                  onChange={(e) => handleUpdateActiveRule({ minPrice: Math.max(0, Number(e.target.value) || 0) })}
                  placeholder="0 (pa limit)"
                />
              </div>

              {/* Category Tree Selector */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="admin-label" style={{ margin: 0 }}>
                    Kategoritë e Përfshira ({(activeRule.categoryIds || []).length} të zgjedhura)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={activeRule.includeSubcategories !== false}
                        onChange={(e) => handleUpdateActiveRule({ includeSubcategories: e.target.checked })}
                      />
                      <span>Përfshi automatikisht nënkategoritë</span>
                    </label>
                    {(activeRule.categoryIds || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleUpdateActiveRule({ categoryIds: [] })}
                        style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Pastro të gjitha
                      </button>
                    )}
                  </div>
                </div>

                <input
                  className="admin-input"
                  placeholder="Filtro kategoritë..."
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  style={{ marginBottom: 8 }}
                />

                <div style={{
                  maxHeight: 220,
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: 8,
                  background: '#fafafa'
                }}>
                  {filteredCategories.map(cat => {
                    const isChecked = (activeRule.categoryIds || []).includes(cat.id);
                    return (
                      <label
                        key={cat.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 8px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          marginLeft: cat.depth * 18,
                          background: isChecked ? '#e0f2fe' : 'transparent',
                          fontSize: 13,
                          color: isChecked ? '#0369a1' : '#334155'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const cur = new Set(activeRule.categoryIds || []);
                            if (checked) cur.add(cat.id);
                            else cur.delete(cat.id);
                            handleUpdateActiveRule({ categoryIds: Array.from(cur) });
                          }}
                        />
                        <span style={{ fontWeight: cat.depth === 0 ? 700 : (isChecked ? 600 : 400) }}>
                          {cat.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* SKU List */}
              <div>
                <label className="admin-label">
                  Kode Specifike SKU ({(activeRule.skus || []).length} të specifikuara)
                </label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <textarea
                    className="admin-input"
                    placeholder="Ngjitni një ose disa kode SKU (të ndara me presje, hapësirë ose rresht të ri)..."
                    value={bulkSkuInput}
                    onChange={(e) => setBulkSkuInput(e.target.value)}
                    rows={2}
                    style={{ flex: 1, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <button
                    type="button"
                    onClick={handleAddBulkSkus}
                    style={{
                      background: '#007da5',
                      color: '#fff',
                      border: 'none',
                      padding: '0 16px',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer'
                    }}
                  >
                    Shto SKU
                  </button>
                </div>

                {activeRule.skus && activeRule.skus.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <input
                        className="admin-input"
                        placeholder="Kërko në listën e SKU-ve..."
                        value={skuSearch}
                        onChange={(e) => setSkuSearch(e.target.value)}
                        style={{ maxWidth: 220, padding: '4px 8px', fontSize: 12 }}
                      />
                      <button
                        type="button"
                        onClick={() => handleUpdateActiveRule({ skus: [] })}
                        style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Hiq të gjitha SKU-të
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      maxHeight: 120,
                      overflowY: 'auto',
                      padding: 8,
                      background: '#f8fafc',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0'
                    }}>
                      {activeRule.skus
                        .filter(s => !skuSearch.trim() || s.toLowerCase().includes(skuSearch.toLowerCase().trim()))
                        .map(sku => (
                          <span
                            key={sku}
                            style={{
                              background: '#fff',
                              border: '1px solid #cbd5e1',
                              borderRadius: 6,
                              padding: '2px 8px',
                              fontSize: 11.5,
                              fontFamily: 'monospace',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            <span>{sku}</span>
                            <span
                              onClick={() => handleRemoveSku(sku)}
                              style={{ color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
                              title="Hiq SKU"
                            >
                              ×
                            </span>
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* RIGHT: Live Interactive Preview Panel */}
          <div style={{ position: 'sticky', top: 20 }}>
            <div className="admin-card" style={{
              padding: 20,
              background: '#fff',
              borderRadius: 14,
              border: '1.5px solid #007da5',
              boxShadow: '0 8px 24px rgba(0, 125, 165, 0.08)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                  👁️ Pamja Direkte (Live Preview)
                </h4>

                {/* Switcher Card vs ProductView */}
                <div style={{ display: 'flex', background: '#f1f5f9', padding: 3, borderRadius: 8 }}>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('card')}
                    style={{
                      border: 'none',
                      background: previewMode === 'card' ? '#fff' : 'transparent',
                      color: previewMode === 'card' ? '#007da5' : '#64748b',
                      fontWeight: 700,
                      fontSize: 12,
                      padding: '4px 10px',
                      borderRadius: 6,
                      cursor: 'pointer'
                    }}
                  >
                    Karta (Card)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('productView')}
                    style={{
                      border: 'none',
                      background: previewMode === 'productView' ? '#fff' : 'transparent',
                      color: previewMode === 'productView' ? '#007da5' : '#64748b',
                      fontWeight: 700,
                      fontSize: 12,
                      padding: '4px 10px',
                      borderRadius: 6,
                      cursor: 'pointer'
                    }}
                  >
                    Product View
                  </button>
                </div>
              </div>

              {/* Price slider simulator */}
              <div style={{ marginBottom: 14, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: '#64748b' }}>Simulo Çmimin e Produktit:</span>
                  <strong style={{ color: '#007da5' }}>{Number(previewPrice).toLocaleString('sq-AL')} Lekë</strong>
                </div>
                <input
                  type="range"
                  min="6000"
                  max="180000"
                  step="3000"
                  value={previewPrice}
                  onChange={(e) => setPreviewPrice(Number(e.target.value))}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
              </div>

              {/* LIVE SIMULATION RENDER */}
              {previewMode === 'card' ? (
                /* Card Simulation */
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 16,
                  background: '#ffffff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                  position: 'relative',
                  maxWidth: 280,
                  margin: '0 auto'
                }}>
                  {/* Badge */}
                  {sampleEvaluation && (
                    <ProductLoanBadge loanInfo={sampleEvaluation} placement="card" isPreview={true} />
                  )}

                  <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                    <img
                      src={sampleCardImg}
                      alt="Sample"
                      style={{ maxHeight: 130, objectFit: 'contain' }}
                    />
                  </div>

                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>Shembull Brand</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '4px 0 8px', lineHeight: 1.3 }}>
                    Smart TV LED 55" 4K Ultra HD
                  </div>

                  {/* Price Section */}
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#007da5' }}>
                      {Number(previewPrice).toLocaleString('sq-AL')} Lekë
                    </div>

                    {/* Subprice text */}
                    {activeRule.card.enabled && (activeRule.card.displayMode === 'subprice' || activeRule.card.displayMode === 'both') && (
                      <div style={{
                        marginTop: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#0284c7',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <span>💳</span>
                        <span>{sampleEvaluation?.card?.subpriceText}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 12, background: '#007da5', color: '#fff', textAlign: 'center', padding: '8px 0', borderRadius: 6, fontWeight: 700, fontSize: 12 }}>
                    BLI TANI
                  </div>
                </div>
              ) : (
                /* Product View Simulation */
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 16,
                  background: '#ffffff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
                }}>
                  {/* Image section with optional badge */}
                  <div style={{
                    position: 'relative',
                    height: 140,
                    background: '#f8fafc',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 14,
                    overflow: 'hidden'
                  }}>
                    {sampleEvaluation && (
                      <ProductLoanBadge loanInfo={sampleEvaluation} placement="productView" isPreview={true} />
                    )}
                    <img
                      src={sampleCardImg}
                      alt="Sample"
                      style={{ maxHeight: 110, objectFit: 'contain' }}
                    />
                  </div>

                  <div style={{ fontSize: 13, color: '#64748b' }}>Çmimi i vetëm:</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>
                    {Number(previewPrice).toLocaleString('sq-AL')} Lekë
                  </div>

                  {/* Render ProductLoanWidget inside simulated Buybox */}
                  {sampleEvaluation && (
                    <ProductLoanWidget loanInfo={sampleEvaluation} />
                  )}
                </div>
              )}

              {/* Quick Info box */}
              <div style={{
                marginTop: 16,
                padding: '10px 12px',
                background: '#f8fafc',
                borderRadius: 8,
                fontSize: 12,
                color: '#64748b',
                lineHeight: 1.4
              }}>
                💡 <strong>Këshillë:</strong> Ndryshimet në tekst dhe afate pasqyrohen menjëherë në këtë pamje. Klikoni <strong>"Ruaj Ndryshimet"</strong> lart për t'i bërë efektive në të gjithë faqen.
              </div>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
